<script setup>
import { userAuthState } from '@/@core/auth'
import TicketStatusGraph from '@/@core/components/department/TicketStatusGraph.vue'
import AnalyticsExperienceBreakdown from '@/@core/components/feedback/AnalyticsExperienceBreakdown.vue'
import AnalyticsNPSBreakdown from '@/@core/components/feedback/AnalyticsNPSBreakdown.vue'
import AnalyticsSourceTypeTracker from '@/@core/components/feedback/AnalyticsSourceTypeTracker.vue'
import AnalyticsUserWiseFeedback from '@/@core/components/feedback/AnalyticsUserWiseFeedback.vue'
import DashboardTicketTypeCard from '@/@core/components/feedback/DashboardTicketTypeCard.vue'
import DashboardUnitOverviewCard from '@/@core/components/feedback/DashboardUnitOverviewCard.vue'
import DepartmentWiseRating from '@/@core/components/feedback/DepartmentWiseRating.vue'
import FeedbackMonthlyAnalysis from '@/@core/components/feedback/FeedbackMonthlyAnalysis.vue'
import LocationHierarchyExplorer from '@/@core/components/feedback/LocationHierarchyExplorer.vue'
import { endOfMonth, startOfMonth } from '@/@core/utils/formatters'
import { can } from '@/@layouts/plugins/casl'
import { cookieRef } from '@/@layouts/stores/config'
import FeedbackTable from '@/views/feedback/feedback-panel/FeedbackTable.vue'
import TicketTable from '@/views/feedback/ticket-management/TicketTable.vue'
import { computed } from 'vue'

definePage({
  meta: {
    name: 'dashboard-location',
    subject: 'feedback.menu',
    action: 'dashboard_location',
  },
})

const auth = userAuthState()
const canAccessUnit = can('read', 'master.unit')

const { data: unitsData } = await useApi(
  createUrl('/masters/units', {
    query: {
      paged: 1,
      select: ['id', 'name', 'display_name'],
    },
  }),
  {
    beforeFetch: ({ cancel }) => {
      if (!canAccessUnit) cancel()
    },
  },
)

const units = computed(() => {
  if (unitsData.value?.data) {
    return unitsData.value?.data
  }

  return [{
    id: auth?.user?.unit_id,
    name: auth?.user?.unit_name,
    display_name: auth?.user?.unit_display_name ?? auth?.user?.unit_name,
  }]
})

const unitId = ref(cookieRef('selectedUnit') ?? null)

// The explorer names its breadcrumb root after the unit, using the unit's display
// name. Compared loosely because the selected unit arrives from a cookie as a
// string while the master list carries numeric ids. The backend backfills
// display_name from name on create, but older rows can still hold null, so fall
// back rather than render an empty crumb.
const unitName = computed(() => {
  const unit = units.value.find(item => String(item.id) === String(unitId.value))

  return unit?.display_name || unit?.name || ''
})

// Location Type
const locationTypeId = ref(null)

const {
  data: locationTypes,
} = await useApi(
  createUrl('/masters/location-types', {
    query: {
      search: {
        unit_id: unitId,
      },
      select: ['id', 'name'],
    },
  }),
)

// Locations
const location = ref(null)
const locations = ref([])
const searchLocation = ref()

// Locations are scoped to a location type, so there is nothing worth fetching
// until one is picked.
const {
  isFetching: fetchingLocation,
} = await useApi(createUrl('/masters/locations', {
  query: {
    paged: 1,
    itemsPerPage: 15,
    q: searchLocation,
    search: {
      unit_id: unitId,
      location_type_id: locationTypeId,
    },
    select: ['id', 'name'],
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!locationTypeId.value) cancel()
  },
  afterFetch: ({ data }) => {
    locations.value = data.data
  },
})

const locationId = computed(() => location.value?.id)

const locationTypeSelected = computed(() => !!locationTypeId.value)

const locationHint = 'Please select location type to fetch the locations'

watch(() => locationTypeId.value, () => {
  location.value = null

  // Only write when there is something to clear — an unconditional reset
  // changes the ref and triggers a second, redundant fetch.
  if (searchLocation.value) searchLocation.value = null
  if (!locationTypeId.value) locations.value = []
}, { deep: true })

const departmentId = ref(null)

// Date utilities
const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)

  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const formatFloat = number => {
  if (Number(number) === number && number % 1 !== 0)
    return number.toFixed(2)

  return number
}

// Date range state
const quickDateRangeOptions = [
  {
    title: 'Today',
    value: `${customDateFormat(new Date())}`,
  },
  {
    title: 'Yesterday',
    value: `${customDateFormat(addDays(new Date(), -1))}`,
  },
  {
    title: 'Last 7 days',
    value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last 30 days',
    value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'This month',
    value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last month',
    value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
  },
  {
    title: 'Custom',
    value: null,
  },
]

const quickDateRange = ref('Last 30 days')

const setQuickDateRange = value => {
  dateRange.value = value?.value
}

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  enableSeconds: false,
  noCalendar: false,
  altInput: true,
  altFormat: 'F j, Y',
  altInputClass: 'form-control',
  altInputPlaceholder: 'Select date',
  inline: false,
  static: false,
  appendTo: document.body,
  clickOpens: true,
  onChange: (selectedDates, dateStr, instance) => {
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1])
      toDate.value = customDateFormat(selectedDates[1])
    else
      toDate.value = customDateFormat(selectedDates[0])

    dateRange.value = dateStr
  },
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
})

watch(dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const dateRangeValue = computed(() => {
  return [fromDate.value, toDate.value]
})

// API calls
// Every widget on this page rolls up the selected location's whole subtree, so each request
// carries include_child_locations. Nothing is fetched until a location is chosen.
const cancelWithoutLocation = ({ cancel }) => {
  if (!locationId.value) cancel()
}

const { data: totalFeedbackData } = useApi(
  createUrl('/dashboard/feedbacks/total-count', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
        location_id: locationId,
      },
      include_child_locations: 1,
    },
  }),
  { beforeFetch: cancelWithoutLocation },
)

const { data: totalTicketTypeData } = useApi(
  createUrl('/dashboard/tickets/ticket-type', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
        location_id: locationId,
      },
      include_child_locations: 1,
    },
  }),
  { beforeFetch: cancelWithoutLocation },
)

const { data: departmentPSatData } = useApi(
  createUrl('/dashboard/feedbacks/p-sat/department', {
    query: {
      search: {
        unit_id: unitId,
        created_at: dateRangeValue,
        location_id: locationId,
      },
      include_child_locations: 1,
    },
  }),
  { beforeFetch: cancelWithoutLocation },
)

// P-Sat computed
const pSatData = computed(() => {
  if (!departmentPSatData.value) return null

  const data = departmentPSatData.value.reduce(
    (acc, obj) => {
      acc.overall_p_sat += obj.overall_p_sat || 0
      acc.p_sat_percentage += obj.p_sat_percentage || 0

      return acc
    },
    { overall_p_sat: 0, p_sat_percentage: 0 },
  )

  const count = departmentPSatData.value?.length > 0 ? departmentPSatData.value.length : 1

  return {
    overall_p_sat: formatFloat(data?.overall_p_sat / count) ?? null,
    p_sat_percentage: formatFloat(data?.p_sat_percentage / count) ?? 0,
  }
})

// Feedback table dialog
const feedbackTableFilter = ref(null)
const isFeedbackTableDialogVisible = ref(false)

const feedbackTableIncludeChildren = ref(true)

const onUpdateFeedbackTableFilter = data => {
  const { include_child_locations: includeChildren = true, ...filters } = data ?? {}

  feedbackTableIncludeChildren.value = includeChildren

  feedbackTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unitId.value,
    location_id: locationId.value,
    ...filters,
  }
  isFeedbackTableDialogVisible.value = true
}

watch(() => isFeedbackTableDialogVisible.value, value => {
  if (value === false) feedbackTableFilter.value = null
})

// Ticket table dialog
const ticketTableFilter = ref(null)
const isTicketTableDialogVisible = ref(false)

const ticketTableIncludeChildren = ref(true)

const onUpdateTicketTableFilter = data => {
  const { include_child_locations: includeChildren = true, ...filters } = data ?? {}

  ticketTableIncludeChildren.value = includeChildren

  ticketTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unitId.value,
    status: [],
    ticket_type: ['COMPLAINT', 'COMPLIMENT', 'SUGGESTION', 'SPECIAL_REQUEST'],
    department_id: departmentId.value,
    location_id: locationId.value,
    ...filters,
  }
  isTicketTableDialogVisible.value = true
}

watch(() => isTicketTableDialogVisible.value, value => {
  if (value === false) ticketTableFilter.value = null
})
</script>

<template>
  <div>
    <div class="text-h4 mb-6">
      Location Dashboard
    </div>

    <!-- Filter Card -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <AppSelect
              v-model="quickDateRange"
              label="Quick Range"
              return-object
              :item-title="item => item.title"
              :item-value="item => item.title"
              :items="quickDateRangeOptions"
              @update:model-value="setQuickDateRange"
            />
          </VCol>
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <AppDateTimePicker
              v-model="dateRange"
              label="Range"
              placeholder="Select date"
              :config="dateRangeOptions"
            />
          </VCol>
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <AppCombobox
              v-model="locationTypeId"
              :items="locationTypes"
              :item-title="item => item.name"
              :item-value="item => item.id"
              label="Location Type"
              placeholder="Select By Location Type"
              :multiple="false"
              :return-object="false"
              :clearable="true"
              @click:clear="locationTypeId = null"
            />
          </VCol>
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <AppAutocomplete
              v-model="location"
              v-model:search="searchLocation"
              :loading="fetchingLocation"
              :items="locations"
              :item-title="item => item.name"
              :item-value="item => item.id"
              :return-object="true"
              :eager="true"
              :clearable="true"
              placeholder="Search & Select By Location"
              label="Location"
              :menu-props="{ maxHeight: '200px' }"
            >
              <!--
                Slot rather than no-data-text: the menu is shrink-to-fit, so a
                single-line message renders wider than the field. Vuetify pins the
                menu's min-width to the field width, so zeroing the title's own width
                lets it fill exactly that and wrap - no breakpoint-specific pixel cap.
              -->
              <template #no-data>
                <VListItem>
                  <VListItemTitle
                    class="text-wrap"
                    style="inline-size: 0; min-inline-size: 100%;"
                  >
                    {{ locationTypeSelected ? 'No data available' : locationHint }}
                  </VListItemTitle>
                </VListItem>
              </template>
            </AppAutocomplete>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!--
      Location Explorer: unlike the widgets below, this one does not need a
      selected location - it starts at the top of the hierarchy and is how you
      find the location worth selecting. 
    -->
    <VRow
      v-if="can('view', 'dashboard.widget.location_hierarchy_explorer')"
      class="mb-6"
    >
      <VCol cols="12">
        <!--
          Deliberately not wrapped in AppLazy. Its VLazy + Suspense pairing does not
          propagate updated slot props, which is why every sibling widget below puts
          fromDate/toDate in its :key - the remount is what delivers new dates. This
          widget holds drill state that a remount would discard, so it takes the
          props directly instead. It sits above the fold and never awaits at top
          level, so it gains nothing from lazy mounting anyway.
        -->
        <LocationHierarchyExplorer
          :key="`lhe-${unitId}`"
          :from="fromDate"
          :to="toDate"
          :unit-id="unitId"
          :unit-name="unitName"
          :location-id="locationId"
          @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          @update:ticket-table-filter="onUpdateTicketTableFilter"
        />
      </VCol>
    </VRow>

    <!-- Analytics Cards: only once a location is selected -->
    <template v-if="location">
      <VRow class="match-height">
        <!-- Feedback Overview + P-Sat -->
        <VCol
          cols="12"
          md="8"
          style="animation-delay: 0ms;"
        >
          <DashboardUnitOverviewCard
            :feedback-data="totalFeedbackData"
            :p-sat-data="pSatData"
          />
        </VCol>

        <!-- Tickets By Type -->
        <VCol
          cols="12"
          md="4"
          style="animation-delay: 70ms;"
        >
          <DashboardTicketTypeCard :data="totalTicketTypeData" />
        </VCol>

        <VCol
          v-if="can('view', 'dashboard.widget.nps_score')"
          cols="12"
          md="6"
        >
          <AppLazy :key="`nps-${unitId}-${locationId}-${fromDate}-${toDate}`">
            <AnalyticsNPSBreakdown
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>

        <VCol
          v-if="can('view', 'dashboard.widget.experience_breakdown')"
          cols="12"
          md="6"
        >
          <AppLazy :key="`exp-${unitId}-${locationId}-${fromDate}-${toDate}`">
            <AnalyticsExperienceBreakdown
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>

        <VCol
          v-if="can('view', 'dashboard.widget.department_wise_rating')"
          cols="12"
        >
          <AppLazy :key="`dwr-${unitId}-${locationId}-${fromDate}-${toDate}`">
            <DepartmentWiseRating
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
            />
          </AppLazy>
        </VCol>

        <!-- Chart Widgets -->
        <VCol
          v-if="can('view', 'dashboard.widget.feedback_monthly_analysis')"
          cols="12"
        >
          <AppLazy :key="`${unitId}-${locationId}`">
            <FeedbackMonthlyAnalysis
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
            />
          </AppLazy>
        </VCol>

        <VCol
          v-if="can('view', 'dashboard.widget.feedback_source_tracker')"
          cols="12"
        >
          <AppLazy :key="`src-${unitId}-${locationId}-${fromDate}-${toDate}`">
            <AnalyticsSourceTypeTracker
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>

        <VCol
          v-if="can('view', 'dashboard.widget.user_wise_feedback')"
          cols="12"
        >
          <AppLazy :key="`usr-${unitId}-${locationId}-${fromDate}-${toDate}`">
            <AnalyticsUserWiseFeedback
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>

        <VCol
          v-if="can('view', 'department_dashboard.widget.ticket_status')"
          cols="12"
        >
          <AppLazy :key="`${unitId}-${locationId}-${fromDate}-${toDate}`">
            <TicketStatusGraph
              :from="fromDate"
              :to="toDate"
              :unit-id="unitId"
              :location-id="locationId"
              :include-child-locations="true"
              @update:ticket-table-filter="onUpdateTicketTableFilter"
            />
          </AppLazy>
        </VCol>
      </VRow>
    </template>

    <!-- Empty State: No Location Selected -->
    <template v-else>
      <VRow>
        <VCol cols="12">
          <VCard
            variant="flat"
            class="border"
          >
            <VCardText class="text-center pa-8">
              <VAvatar
                color="primary"
                variant="tonal"
                size="64"
                class="mb-4"
              >
                <VIcon
                  icon="tabler-map-pin"
                  size="36"
                />
              </VAvatar>
              <h6 class="text-h6 mb-2">
                Select a Location
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-0">
                Choose a location from the filter above to view analytics
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>

  <!-- Ticket Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <TicketTable
          ref="refTicketTable"
          page="ticket-management"
          :ticket-filters="ticketTableFilter"
          :include-child-locations="ticketTableIncludeChildren"
          :action-visible="false"
          :style="{
            cardVariant: 'flat',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Feedback Table Dialog -->
  <VDialog v-model="isFeedbackTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isFeedbackTableDialogVisible = !isFeedbackTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <FeedbackTable
          :feedback-filters="feedbackTableFilter"
          :include-child-locations="feedbackTableIncludeChildren"
          :action-visible="false"
          :style="{
            cardVariant: 'flat',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
